import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { HttpClient, HttpResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { map, catchError } from "rxjs/operators";
//import 'rxjs/add/observable/from';

import { ResultCache } from '../models/result-cache';
import { TreeViewGroupedData } from '../models/tree-view-grouped-data';
import { ParamData } from '../models/param-data';
import { TreeViewSingleData } from '../models/tree-view-single-data';
import { ServerTreeViewData } from '../models/server-tree-view-data';
import { ParamPresentation } from '../models/tree-view-data-base';
import { ChartConfigBase } from "../models/chart-config-base";
import { ParametersConfig } from '../models/parameters-config';

@Injectable({ providedIn: 'root' })
export class ParametersDataService {

    private cachedUrls: ResultCache[] = [];
    private acceptCache: boolean = true;

    constructor(
        private location: Location,
        private http: HttpClient
    ) {
        this.setDefaultChartDataTypes();
    }

    /**
     * Hämtar ut cachad data för en url
     * @param url
     */
    private getCache(url: string): ResultCache {
        let foundIndex = this.cachedUrls.findIndex((element) => {
            return element.url === url;
        });
        if (foundIndex < 0)
            return null;
        return this.cachedUrls[foundIndex];
    }

    /**
     * Sätter cachad data för en url
     * @param url
     * @param data
     */
    private setCache(url: string, data: any): void {
        let existing = this.getCache(url);
        if (existing) {
            existing.data = data;
        } else {
            this.cachedUrls.push(new ResultCache(url, data));
        }
    }

    /**
     * Sätter om "getParameterData()" får acceptera cachad data
     * @param accept
     */
    public setAcceptCache(accept: boolean): void {
        this.acceptCache = accept;
    }

    /**
     * Hämtar filterparametrar från backend.
     * @param config
     */
    public getParameterData(config: ParametersConfig): Observable<HttpResponse<any>> {
        try {
            let params: HttpParams = new HttpParams();
            Object.keys(config).map((key) => {
                // Använd alla egenskaper på config-objektet i query-string
                params = params.set(key, config[key]);
            });
            let existing = this.getCache(config.serverUrl + params.toString());
            if (existing && this.acceptCache) {
                return from([existing.data]);
            }

            return this.http.get(config.serverUrl, { params: params })
                .pipe(map((response: HttpResponse<any>) => {
                    this.setCache(config.serverUrl + params.toString(), response);
                    return response;
                }))
                .pipe(catchError(err => this.handleServerApiError(err)));
        } catch (e) {
            return throwError(() => new Error(e));
        }
    }

    /**
     * Hämtar filterparameters id från backend.
     * @param serverUrl
     * @param parameter
     * @param value
     */
    public getParameterId(apiBaseUrl: string, serverUrl: string, parameter: string, value: string): Observable<HttpResponse<any>> {
        try {
            return this.http.get(apiBaseUrl + serverUrl + `?data=${parameter}&value=${value}`)
                .pipe(map((response: HttpResponse<any>) => {
                    return response;
                }))
                .pipe(catchError(err => this.handleServerApiError(err)));
        } catch (e) {
            return throwError(() => new Error(e));
        }
    }

    protected handleServerApiError(errorResponse: HttpResponse<any>) {
        return throwError(() => new Error(errorResponse['error']));
    }

    private paramPresentationDataSets = {};
    public getParamPresentationDataSets(treeViewDataId: string): ParamPresentation {
        return this.paramPresentationDataSets[treeViewDataId];
    }

    private treeViewGroupDatas = {};
    public getParamPresentationDataSetsForGroups(treeViewDataId: string, groupName: string): ParamPresentation {
        return this.treeViewGroupDatas[treeViewDataId].getParamPresentationForGroup(groupName);
    }

    public setServerSingleData(treeViewData: TreeViewSingleData, serverData: ServerTreeViewData[], paramData: ParamData, collapseFirstLevel: boolean = false): void {
        treeViewData.updatingParam = true; // Stäng av automatisk uppdatering av rapport via selectedChange under tiden komponenten uppdateras med data.
        treeViewData.serverData = serverData;
        treeViewData.setItems(paramData, collapseFirstLevel);
        setTimeout(() => {
            treeViewData.updatingParam = false; // Aktivera automatisk uppdatering via selectedChange igen.
        });
        this.paramPresentationDataSets[treeViewData.id] = treeViewData.getParamPresentation();
    }

    public loadAvdelningar(treeViewData: TreeViewGroupedData, paramData: ParamData, config: ParametersConfig): void {
        let totalCount = treeViewData.treeViewGroupData.length;
        treeViewData.treeViewGroupData.forEach((groupData) => {
            config.data = groupData.dataType;
            let getTree = true;
            this.getParameterData(config).subscribe((data: any) => {
                groupData.dataobj = data;
                if (!getTree) {
                    if (config.data !== 'avdelningar') {
                        groupData.dataGroups = this.groupBy(groupData.dataobj, 'Region');
                    } else {
                        groupData.dataobj.forEach((innerObj: any) => {
                            innerObj.RegKod = innerObj.Value.substring(0, 1);
                        });
                        groupData.dataGroups = this.groupBy(groupData.dataobj, 'RegKod');
                    }
                } else {
                    groupData.dataGroups = groupData.dataobj;
                }

                totalCount--; // Sätt på automatisk uppdatering via selectedChange igen när alla parametrar är laddade.
                this.setUpdatingParam(totalCount, treeViewData, paramData);
            });
        });
    }

    public loadAldersgrupper(treeViewData: TreeViewGroupedData, paramData: ParamData, config: ParametersConfig): void {
        let totalCount = treeViewData.treeViewGroupData.length;
        treeViewData.treeViewGroupData.forEach((groupData) => {
            config.data = groupData.dataType;
            config.gettree = true;
            this.getParameterData(config).subscribe((data) => {
                groupData.dataGroups = data;

                totalCount--; // Sätt på automatisk uppdatering via selectedChange igen när alla parametrar är laddade.
                this.setUpdatingParam(totalCount, treeViewData, paramData);
            });
        });
    }
    
    public loadTreeViewSingleData(config: ParametersConfig, configId: string, treeViewData: TreeViewSingleData, paramData: ParamData): void {
        let configCopy = Object.assign({}, config);
        configCopy.data = configId;
        configCopy.gettree = true;
        this.getParameterData(config).subscribe((data: any) => {
            treeViewData.serverData = data;
            this.setUpdatingParam(0, treeViewData, paramData);
        });
    }

    public getDefaultConfig(dataType: string, getTree: boolean): ParametersConfig {
        let config = new ParametersConfig();
        config.data = dataType;
        config.gettree = getTree;
        config.format = 'json';
        return config;
    }

    private setUpdatingParam(totalCount: number, treeViewData: any, paramData: ParamData): void {
        if (totalCount === 0) {
            treeViewData.updatingParam = true;
            treeViewData.setItems(paramData);
            setTimeout(() => {
                treeViewData.updatingParam = false;
            });
            this.paramPresentationDataSets[treeViewData.id] = treeViewData.getParamPresentation();
            if (treeViewData.treeViewGroupData)
                this.treeViewGroupDatas[treeViewData.id] = treeViewData;
        }
    }

    /**
     * Group array by property to associative array
     * @param arr
     * @param prop
     */
    private groupBy(arr: any[], prop: string): any[] {
        return arr.reduce((rv, obj) => {
            (rv[obj[prop]] = rv[obj[prop]] || []).push(obj);
            return rv;
        }, {});
    }

    private defaultChartDataTypes = {};

    private setDefaultChartDataTypes(): void {
        this.defaultChartDataTypes['Region'] = ['avd'];
        this.defaultChartDataTypes['Landsting'] = ['avd'];
        this.defaultChartDataTypes['Sjukvårdsregion'] = ['avd'];
        this.defaultChartDataTypes['Healthcare region'] = ['avd'];
        this.defaultChartDataTypes['Avdelning'] = ['avd'];
        this.defaultChartDataTypes['Unit'] = ['avd'];
        this.defaultChartDataTypes['Riket'] = ['avd'];
        this.defaultChartDataTypes['Nation'] = ['avd'];
        this.defaultChartDataTypes['Sjukhustyp'] = ['sjukhustyp'];
        this.defaultChartDataTypes['Hospital type'] = ['sjukhustyp'];
        this.defaultChartDataTypes['År'] = ['period'];
        this.defaultChartDataTypes['Year'] = ['period'];
        this.defaultChartDataTypes['Tertial'] = ['period'];
        this.defaultChartDataTypes['Tertials'] = ['period'];
        this.defaultChartDataTypes['Tertial konsekutivt'] = ['period'];
        this.defaultChartDataTypes['Tertials consecutive'] = ['period'];
        this.defaultChartDataTypes['Kvartal'] = ['period'];
        this.defaultChartDataTypes['Trimester'] = ['period'];
        this.defaultChartDataTypes['Kvartal konsekutivt'] = ['period'];
        this.defaultChartDataTypes['Trimester consecutive'] = ['period'];
        this.defaultChartDataTypes['Månad'] = ['period'];
        this.defaultChartDataTypes['Month'] = ['period'];
        this.defaultChartDataTypes['Månad konsekutivt'] = ['period'];
        this.defaultChartDataTypes['Month consecutive'] = ['period'];
    }

    public getChartDataTypes(grouping: string): string[] {
        return this.defaultChartDataTypes[grouping];
    }

    public addChartDataType(grouping: string, types: string[]): void {
        this.defaultChartDataTypes[grouping] = types;
    }

    public convertPeriodToDates(periodType: string, periodString: any, chartConfig: ChartConfigBase, chartType?: string): void {
        let startdat, stopdat: string = '';
        let year;
        switch (periodType) {
            case 'År':
            case 'Year':
                if (chartType == 'datetime') {
                    //Om "chartType" är "datetime", då är "periodString" av type Date
                    startdat = `${periodString.getFullYear()}-01-01`;
                    stopdat = `${periodString.getFullYear()}-12-31`;
                } else {
                    startdat = `${periodString}-01-01`;
                    stopdat = `${periodString}-12-31`;
                } 
                break;
            case 'Tertial':
            case 'Tertial konsekutivt':
            case 'Tertials':
            case 'Tertials consecutive':
                let tertial = periodString.split(' ')[0];
                year = periodString.split(' ')[1];
                switch (tertial) {
                    case '1:a':
                    case '1st':
                        startdat = `${year}-01-01`;
                        stopdat = `${year}-04-30`;
                        break;
                    case '2:a':
                    case '2nd':
                        startdat = `${year}-05-01`;
                        stopdat = `${year}-08-31`;
                        break;
                    case '3:e':
                    case '3rd':
                        startdat = `${year}-09-01`;
                        stopdat = `${year}-12-31`;
                        break;
                    default: break;
                }
                break;
            case 'Kvartal':
            case 'Kvartal konsekutivt':
            case 'Trimester':
            case 'Trimester consecutive':
                let kvartal = periodString.split(' ')[0];
                year = periodString.split(' ')[1];
                switch (kvartal) {
                    case '1:a':
                    case '1st':
                        startdat = `${year}-01-01`;
                        stopdat = `${year}-03-31`;
                        break;
                    case '2:a':
                    case '2nd':
                        startdat = `${year}-04-01`;
                        stopdat = `${year}-06-30`;
                        break;
                    case '3:e':
                    case '3rd':
                        startdat = `${year}-07-01`;
                        stopdat = `${year}-09-30`;
                        break;
                    case '4:e':
                    case '4th':
                        startdat = `${year}-10-01`;
                        stopdat = `${year}-12-31`;
                        break;
                    default: break;
                }
                break;
            case 'Månad':
            case 'Månad konsekutivt':
            case 'Month':
            case 'Month consecutive':
                if (chartType == 'datetime') {
                    //Om "chartType" är "datetime", då är "periodString" av type Date
                    const year = periodString.getFullYear();
                    const month = periodString.getMonth() + 1; //zero-index
                    startdat = `${year}-${month<10?'0':''}${month}-01`;
                    stopdat = `${year}-${month<10?'0':''}${month}-${this.getLastDateOfMonth(year, month)}`;
                } else {
                    let month = periodString.split(' ')[0];
                    year = periodString.split(' ')[1];
                    let monthNr: string = '';
                    let lastDay: string = '';
                    switch (month) {
                        case 'Januari':  case 'January':  monthNr = '01'; lastDay = this.getLastDateOfMonth(year, 1); break;
                        case 'Februari': case 'February': monthNr = '02'; lastDay = this.getLastDateOfMonth(year, 2); break;
                        case 'Mars':     case 'March':    monthNr = '03'; lastDay = this.getLastDateOfMonth(year, 3); break;
                        case 'April':                     monthNr = '04'; lastDay = this.getLastDateOfMonth(year, 4); break;
                        case 'Maj':      case 'May':      monthNr = '05'; lastDay = this.getLastDateOfMonth(year, 5); break;
                        case 'Juni':     case 'June':     monthNr = '06'; lastDay = this.getLastDateOfMonth(year, 6); break;
                        case 'Juli':     case 'July':     monthNr = '07'; lastDay = this.getLastDateOfMonth(year, 7); break;
                        case 'Augusti':  case 'August':   monthNr = '08'; lastDay = this.getLastDateOfMonth(year, 8); break;
                        case 'September':                 monthNr = '09'; lastDay = this.getLastDateOfMonth(year, 9); break;
                        case 'Oktober':  case 'October':  monthNr = '10'; lastDay = this.getLastDateOfMonth(year, 10); break;
                        case 'November':                  monthNr = '11'; lastDay = this.getLastDateOfMonth(year, 11); break;
                        case 'December':                  monthNr = '12'; lastDay = this.getLastDateOfMonth(year, 12); break;
                        default: break;
                    }
                    startdat = `${year}-${monthNr}-01`;
                    stopdat = `${year}-${monthNr}-${lastDay}`;
                }
                break;
            default: break;
        }

        chartConfig.startdat = startdat;
        chartConfig.stopdat = stopdat;

    }

    private getLastDateOfMonth(year, month): string {
        return new Date(year, month, 0).getDate().toString();
    }

    public getKeysFromDataItem(dataType: string, clickedDataItem: string): number[] {
        // Försök först hitta Key-Value par från ett enstaka key-value-par från alla data items i TreeView-datan från servern.
        let singleItemKey = this.getParamPresentationDataSets(dataType).getKeyFromValue(clickedDataItem);
        let groupParam: ParamPresentation;

        // Försök därefter hitta en grupp med key-value-par från TreeView-datan från servern.
        if (!singleItemKey || Number(singleItemKey) >= 1000)
            if (this.treeViewGroupDatas[dataType])
                groupParam = this.getParamPresentationDataSetsForGroups(dataType, clickedDataItem);

        // Skapa en number-array med keys
        let resultArray: number[] = [];
        if (singleItemKey && Number(singleItemKey) < 1000) {
            resultArray = [parseInt(singleItemKey)];
        } else if (groupParam) {
            let stringKeys = groupParam.getAllKeys();
            stringKeys.forEach((key) => {
                resultArray.push(+key);
            });
        }
        return resultArray;
    }

}
